import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf, NgClass } from '@angular/common';

@Component({
    selector: 'app-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    standalone: true,
    imports: [NgIf, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, NgClass]
})
export class InputComponent  {
  @Input() form: FormGroup | undefined;
  @Input() formGroupName: string | undefined;
  @Input() placeHolder: string = '';
  @Input() label: string = '';
  // Optional text prefix rendered inside the field (e.g. "$" for money inputs).
  @Input() prefix: string = '';
  @Input() passwordHelpEnabled: boolean = false;
  // 'integer' and 'decimal' render as text inputs with a mobile numeric keyboard
  // and keystroke/paste filtering; 'decimal' allows at most 2 decimal places (money).
  @Input() inputType: 'text' | 'password' | 'email' | 'integer' | 'decimal' | 'tel' = 'text';
  // Optional action button in the field's suffix slot (e.g. 'fa-camera' to scan
  // the value from a photo). Set suffixBusy to show a spinner while it works.
  @Input() suffixIcon: string = '';
  @Input() suffixLabel: string = '';
  @Input() suffixBusy: boolean = false;
  @Output() suffixClick = new EventEmitter<void>();
  viewPassword: boolean = false;

  get nativeType(): string {
    return (this.inputType === 'integer' || this.inputType === 'decimal') ? 'text' : this.inputType;
  }

  get inputModeAttr(): string | null {
    switch (this.inputType) {
      case 'integer': return 'numeric';
      case 'decimal': return 'decimal';
      case 'tel': return 'tel';
      default: return null;
    }
  }

  private get isFiltered(): boolean {
    return this.inputType === 'integer' || this.inputType === 'decimal' || this.inputType === 'tel';
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.isFiltered || event.key.length > 1 || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const value = (event.target as HTMLInputElement).value;
    const blocked =
      (this.inputType === 'integer' && !/\d/.test(event.key)) ||
      (this.inputType === 'decimal' && (!/[\d.]/.test(event.key) || (event.key === '.' && value.includes('.')))) ||
      (this.inputType === 'tel' && !/[\d\s().+-]/.test(event.key));
    if (blocked) {
      event.preventDefault();
    }
  }

  // Authoritative filter: catches paste, drag-drop, IME, and autofill that keydown misses.
  onInput(event: Event): void {
    if (!this.isFiltered) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const sanitized = this.sanitize(input.value);
    if (sanitized !== input.value) {
      const drift = input.value.length - sanitized.length;
      const pos = Math.max((input.selectionStart ?? sanitized.length) - drift, 0);
      this.form?.get(this.formGroupName || '')?.setValue(sanitized);
      input.setSelectionRange(pos, pos);
    }
  }

  private sanitize(value: string): string {
    switch (this.inputType) {
      case 'integer':
        return value.replace(/\D/g, '');
      case 'tel':
        return value.replace(/[^\d\s().+-]/g, '');
      case 'decimal': {
        let v = value.replace(/[^\d.]/g, '');
        const dot = v.indexOf('.');
        if (dot !== -1) {
          v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '');
          v = v.slice(0, dot + 3);
        }
        return v;
      }
      default:
        return value;
    }
  }

  upperRegEx = new RegExp('[A-Z]');
  lowerRegEx = new RegExp('[a-z]');
  numberRegEx = new RegExp('[0-9]');
  specialRegEx = new RegExp('[^a-zA-Z\\d\\s]');

  testMatches(test: RegExp) {
    let formControl = this.form?.get(this.formGroupName || '')?.value;
    if (formControl) {
      return formControl.match(test);
    } else {
      return null;
    }
  }

  toggleViewPassword() {
    this.viewPassword = !this.viewPassword;
  }

  showPasswordHelp() {
    if (this.form && this.formGroupName) {
      let control = this.form.get(this.formGroupName);
      return (this.passwordHelpEnabled == true && ((control?.touched && !control?.valid) || (control?.value.length > 0 && !control?.valid)));
    } else {
      return false;
    }
  }

}
